import {
  apiService,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  ChangePasswordRequest,
  User,
} from "./api";

// Auth service for managing authentication state and operations
export class AuthService {
  private static instance: AuthService;
  private currentUser: User | null = null;
  private isAuthenticated: boolean = false;
  private isInitialized: boolean = false;
  private userDataChangeListeners: Array<() => void> = [];

  private constructor() {
    // Initialize auth state asynchronously
    this.initializeAuth().catch((error) => {
      console.error("Auth initialization failed:", error);
    });
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Initialize authentication state from localStorage
  private async initializeAuth() {
    const token = apiService.getToken();
    if (token) {
      try {
        // Set authenticated state first
        this.isAuthenticated = true;

        // Try to get current user
        const user = await apiService.getCurrentUser();
        if (user) {
          this.currentUser = user;
          console.log("User data restored from API");
        } else {
          // If no user data, try to refresh token
          try {
            await this.refreshToken();
            const refreshedUser = await apiService.getCurrentUser();
            if (refreshedUser) {
              this.currentUser = refreshedUser;
              console.log("User data restored after token refresh");
            }
          } catch (refreshError) {
            console.error(
              "Token refresh failed during initialization:",
              refreshError
            );
            this.logout();
          }
        }
      } catch (error: any) {
        console.error(
          "Failed to get current user during initialization:",
          error
        );
        // Try refresh token before giving up
        try {
          await this.refreshToken();
          const user = await apiService.getCurrentUser();
          if (user) {
            this.currentUser = user;
            console.log("User data restored after refresh");
          } else {
            this.logout();
          }
        } catch (refreshError) {
          console.error(
            "All auth initialization attempts failed:",
            refreshError
          );
          this.logout();
        }
      }
    }

    this.isInitialized = true;
  }

  // Login user
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiService.login(credentials);
      this.currentUser = response.user;
      this.isAuthenticated = true;
      // Notify listeners that user data has changed
      this.notifyUserDataChange();
      return response;
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  // Register user
  async register(userData: RegisterRequest): Promise<LoginResponse> {
    try {
      const response = await apiService.register(userData);
      this.currentUser = response.user;
      this.isAuthenticated = true;
      // Notify listeners that user data has changed
      this.notifyUserDataChange();
      return response;
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await apiService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      this.currentUser = null;
      this.isAuthenticated = false;
      // Notify listeners that user data has changed
      this.notifyUserDataChange();
    }
  }

  // Clear auth state without calling logout API (for auth failures)
  clearAuthState(): void {
    this.currentUser = null;
    this.isAuthenticated = false;
    // Notify listeners that user data has changed
    this.notifyUserDataChange();
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Add listener for user data changes
  addUserDataChangeListener(listener: () => void): () => void {
    this.userDataChangeListeners.push(listener);
    // Return unsubscribe function
    return () => {
      const index = this.userDataChangeListeners.indexOf(listener);
      if (index > -1) {
        this.userDataChangeListeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners that user data has changed
  private notifyUserDataChange(): void {
    this.userDataChangeListeners.forEach((listener) => listener());
  }

  // Check if user is authenticated
  isUserAuthenticated(): boolean {
    return this.isAuthenticated && !!apiService.getToken();
  }

  // Wait for initialization to complete
  async waitForInitialization(): Promise<void> {
    while (!this.isInitialized) {
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
  }

  // Change password
  async changePassword(
    passwordData: ChangePasswordRequest
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.changePassword(passwordData);
      console.log("API response:", response);

      // Check if the response indicates success
      if (response.success === false || !response.success) {
        return {
          success: false,
          message: response.message || "Đổi mật khẩu thất bại",
        };
      }

      return {
        success: true,
        message: response.message || "Đổi mật khẩu thành công",
      };
    } catch (error: any) {
      console.error("Failed to change password:", error);

      // If it's an API error with message, return it as a failed response
      if (error.message) {
        return {
          success: false,
          message: error.message,
        };
      }

      throw error;
    }
  }

  // Update user profile
  async updateProfile(profileData: {
    name: string;
    email: string;
    phone: string;
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.updateProfile(profileData);

      if (response.success) {
        // Refresh current user data after successful update
        try {
          const updatedUser = await apiService.getCurrentUser();
          if (updatedUser) {
            this.currentUser = updatedUser;
            // Notify listeners that user data has changed
            this.notifyUserDataChange();
          }
        } catch (refreshError) {
          console.error(
            "Failed to refresh user data after update:",
            refreshError
          );
          // Don't throw error, just log it
        }
      }

      return {
        success: response.success,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      throw error;
    }
  }

  // Refresh token
  async refreshToken(): Promise<{
    success: boolean;
    message: string;
    user?: any;
  }> {
    try {
      const response = await apiService.refreshToken();

      if (response.success && response.user) {
        // Update current user data
        this.currentUser = {
          _id: response.user.name, // Use name as ID since API doesn't return _id
          name: response.user.name,
          email: response.user.email,
          phone: response.user.phone,
          role_id: response.user.role_id.toString(),
          role_name: response.user.role_name,
          createdAt: response.user.createdAt,
        };
        this.isAuthenticated = true;
      }

      return {
        success: response.success,
        message: response.message,
        user: response.user,
      };
    } catch (error) {
      console.error("Token refresh failed:", error);
      this.logout();
      throw error;
    }
  }

  // Password reset flow
  async sendPasswordResetOTP(email: string): Promise<void> {
    await apiService.sendOTP(email);
  }

  async verifyPasswordResetOTP(email: string, otp: string): Promise<void> {
    await apiService.verifyOTP(email, otp);
  }

  async resetPassword(
    email: string,
    otp: string,
    newPassword: string
  ): Promise<void> {
    await apiService.resetPassword(email, otp, newPassword);
  }

  // Get user role
  getUserRole(): string | null {
    return this.currentUser?.role_name || null;
  }

  // Check if user has specific role
  hasRole(roleName: string): boolean {
    return this.currentUser?.role_name === roleName;
  }

  // Check if user is admin
  isAdmin(): boolean {
    return this.hasRole("Admin");
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();

// Export types for use in components
export type { User, LoginRequest, RegisterRequest, ChangePasswordRequest };
